import { FLUSH_NOTES } from './actionTypes';
import { loadNotesFromStorage, openNotesPane, setNotesText } from './actions';

let attachedWindow;
let disposeWindow;

/**
 * Flushes and closes the window when returning to the meeting or leaving it.
 *
 * @returns {void}
 */
export function closeNotesWindow() {
    disposeWindow?.();
}

/**
 * Opens one attached window per meeting page, or focuses the existing window.
 * Uses the meeting store directly; notes never go through the conference server.
 *
 * @param {Store} store - Meeting Redux store.
 * @param {Function} t - Translation function.
 * @param {Function} onError - Reports a blocked popup or failed bundle load.
 * @returns {void}
 */
export function openNotesWindow(store, t, onError) {
    if (attachedWindow && !attachedWindow.closed) {
        attachedWindow.focus();

        return;
    }
    closeNotesWindow();
    let popup;

    try {
        // A fresh unnamed window avoids attaching private notes to another
        // meeting tab's popup. Our module owns and reuses this window reference.
        popup = window.open('', '_blank', 'popup=yes,width=840,height=760,resizable=yes,scrollbars=yes');
    } catch {
        onError();

        return;
    }
    if (!popup) {
        onError();

        return;
    }

    attachedWindow = popup;
    let view;
    let unsubscribe;
    let checkClosed;
    let loadTimeout;
    let disposed = false;
    const flush = () => store.dispatch({ type: FLUSH_NOTES });
    const dispose = () => {
        if (disposed) {
            return;
        }
        disposed = true;
        unsubscribe?.();
        clearInterval(checkClosed);
        clearTimeout(loadTimeout);
        window.removeEventListener('pagehide', dispose);
        popup.removeEventListener('pagehide', dispose);
        flush();
        try {
            view?.unmount();
        } finally {
            if (!popup.closed) {
                popup.close();
            }
            attachedWindow = undefined;
            disposeWindow = undefined;
        }
    };

    disposeWindow = dispose;
    const fail = () => {
        dispose();
        onError();
    };

    try {
        const doc = popup.document;

        doc.title = t('notesPane.title');
        doc.documentElement.lang = document.documentElement.lang;
        doc.documentElement.dir = document.documentElement.dir;
        const base = doc.createElement('base');

        base.href = document.baseURI;
        doc.head.appendChild(base);
        document.querySelectorAll('link[rel="stylesheet"], style').forEach(style => {
            doc.head.appendChild(style.cloneNode(true));
        });
        doc.body.className = 'notes_window-page';
        const container = doc.createElement('main');

        container.id = 'notes-window-root';
        container.textContent = t('notesPane.windowLoading');
        doc.body.appendChild(container);
        const script = doc.createElement('script');
        const appScript = Array.from(document.scripts).find(item => /\/app\.bundle(?:\.min)?\.js(?:\?|$)/u.test(item.src));

        script.src = appScript ? appScript.src.replace(/app\.bundle(?=(?:\.min)?\.js)/u, 'notes-window')
            : new URL('libs/notes-window.min.js', document.baseURI).href;
        script.onerror = fail;
        script.onload = () => {
            if (disposed) {
                return;
            }
            clearTimeout(loadTimeout);
            if (!popup.MeetHourNotesWindow?.mount) {
                fail();

                return;
            }
            view = popup.MeetHourNotesWindow.mount(container);
            let previous;
            const render = () => {
                const notes = store.getState()['features/notes'];

                if (notes === previous) {
                    return;
                }
                previous = notes;
                view.render({
                    ...notes,

                    // The popup edits only our own notes, but its privacy chip
                    // still has to say when they are shared.
                    privacyLabel: notes.shareAudience === 'everyone'
                        ? t('notesPane.sharedEveryone')
                        : notes.sharedWith.length
                            ? t('notesPane.sharedWith', { count: notes.sharedWith.length }) : undefined,
                    t,
                    onChange: (text, richText) => store.dispatch(setNotesText(text, richText)),
                    onRetry: () => store.dispatch(notes.saveStatus === 'loadError'
                        ? loadNotesFromStorage(notes.roomName) : { type: FLUSH_NOTES }),
                    onReturn: () => {
                        store.dispatch(openNotesPane());
                        window.focus();
                        dispose();
                    }
                });
            };

            unsubscribe = store.subscribe(render);
            render();
        };
        popup.addEventListener('pagehide', dispose);
        window.addEventListener('pagehide', dispose);
        checkClosed = window.setInterval(() => {
            if (popup.closed) {
                dispose();
            }
        }, 500);
        loadTimeout = window.setTimeout(fail, 15000);
        doc.head.appendChild(script);
        popup.focus();
    } catch {
        fail();
    }
}
